'use client'

import React, { useEffect, useState } from 'react'
import { Check, Minus, ChevronDown } from 'lucide-react'
import { useT } from '@/lib/useT'

// Flip to true to fall back to the static plan data below (e.g. if the
// /api/plans DB-backed source has an issue). Keep the hardcoded config
// in place so this is a one-line revert.
const USE_HARDCODED_PLANS = false

interface DbPlan {
  id: number
  name: string
  charge: number
  currencyCode: string
  currencySymbol: string
  features: string[]
}

type PlanValue = boolean | string

interface Row {
  featureKey: string
  detailKey?: string
  starter: PlanValue
  professional: PlanValue
  enterprise: PlanValue
}

interface Group {
  labelKey: string
  rows: Row[]
}

const groupsConfig: Group[] = [
  {
    labelKey: 'comparison.platformLimits',
    rows: [
      {
        featureKey: 'comparison.users',
        detailKey: 'comparison.usersDetail',
        starter: '10',
        professional: '100',
        enterprise: 'comparison.unlimited',
      },
      {
        featureKey: 'comparison.storage',
        detailKey: 'comparison.storageDetail',
        starter: '5 GB',
        professional: '50 GB',
        enterprise: 'comparison.unlimited',
      },
    ],
  },
  {
    labelKey: 'comparison.integration',
    rows: [
      {
        featureKey: 'comparison.apiAccess',
        detailKey: 'comparison.apiAccessDetail',
        starter: 'comparison.readOnly',
        professional: 'comparison.full',
        enterprise: 'Full + custom',
      },
      {
        featureKey: 'comparison.automation',
        detailKey: 'comparison.automationDetail',
        starter: false,
        professional: 'comparison.rules50',
        enterprise: 'comparison.rulesUnlimited',
      },
      {
        featureKey: 'comparison.customIntegrations',
        detailKey: 'comparison.customIntegrationsDetail',
        starter: false,
        professional: false,
        enterprise: true,
      },
    ],
  },
  {
    labelKey: 'comparison.reporting',
    rows: [
      {
        featureKey: 'comparison.reports',
        detailKey: 'comparison.reportsDetail',
        starter: 'comparison.standard',
        professional: 'comparison.advanced',
        enterprise: 'comparison.customBI',
      },
      {
        featureKey: 'comparison.auditLogs',
        detailKey: 'comparison.auditLogsDetail',
        starter: false,
        professional: 'comparison.days30',
        enterprise: 'comparison.rulesUnlimited',
      },
      {
        featureKey: 'comparison.dataExport',
        detailKey: 'comparison.dataExportDetail',
        starter: 'comparison.csv',
        professional: 'comparison.csvExcel',
        enterprise: 'comparison.allFormats',
      },
    ],
  },
  {
    labelKey: 'comparison.security',
    rows: [
      {
        featureKey: 'comparison.sso',
        detailKey: 'comparison.ssoDetail',
        starter: false,
        professional: true,
        enterprise: 'comparison.ssoPlusSaml',
      },
      {
        featureKey: 'comparison.mfa',
        detailKey: 'comparison.mfaDetail',
        starter: false,
        professional: true,
        enterprise: true,
      },
      {
        featureKey: 'comparison.customBranding',
        detailKey: 'comparison.customBrandingDetail',
        starter: false,
        professional: false,
        enterprise: true,
      },
    ],
  },
  {
    labelKey: 'comparison.support',
    rows: [
      {
        featureKey: 'comparison.prioritySupport',
        detailKey: 'comparison.prioritySupportDetail',
        starter: false,
        professional: true,
        enterprise: true,
      },
      {
        featureKey: 'comparison.dedicatedOnboarding',
        detailKey: 'comparison.dedicatedOnboardingDetail',
        starter: false,
        professional: false,
        enterprise: true,
      },
      {
        featureKey: 'comparison.supportChannel',
        detailKey: 'comparison.supportChannelDetail',
        starter: 'Email',
        professional: 'comparison.emailPhone',
        enterprise: '24/7 SLA',
      },
    ],
  },
]

function Cell({ value, featured, t }: { value: PlanValue; featured?: boolean; t: (key: string) => string }) {
  if (value === true)
    return (
      <span className="flex justify-center" aria-label="Included">
        <span
          className={`inline-flex items-center justify-center h-6 w-6 rounded-full ${
            featured ? 'bg-[#FFBD76]/20' : 'bg-[#E4ECED]'
          }`}
        >
          <Check
            size={12}
            strokeWidth={3}
            className={featured ? 'text-[#FFBD76]' : 'text-[#003F47]'}
            aria-hidden="true"
          />
        </span>
      </span>
    )

  if (value === false)
    return (
      <span className="flex justify-center" aria-label="Not included">
        <span
          className={`inline-flex items-center justify-center h-6 w-6 rounded-full ${
            featured ? 'bg-[#FFBD76]/10' : 'bg-[#E4ECED]/50'
          }`}
        >
          <Minus
            size={12}
            strokeWidth={3}
            className={featured ? 'text-[#FFBD76]/40' : 'text-[#C5C8C9]'}
            aria-hidden="true"
          />
        </span>
      </span>
    )

  const displayValue = typeof value === 'string' && value.startsWith('comparison.') ? t(value as any) : value
  return (
    <span className={`text-sm font-medium ${featured ? 'text-[#FFBD76]' : 'text-[#003F47]'}`}>
      {displayValue}
    </span>
  )
}

function HardcodedComparisonTable() {
  const t = useT()
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    new Set(groupsConfig.map((g) => g.labelKey))
  )

  function toggleGroup(label: string) {
    setExpandedGroups((prev) => {
      const next = new Set(prev)
      if (next.has(label)) {
        next.delete(label)
      } else {
        next.add(label)
      }
      return next
    })
  }

  return (
    <section
      id="comparison"
      className="py-24 bg-[#FFF6E9]"
      aria-labelledby="comparison-heading"
    >
      <div className="mx-auto max-w-[1200px] px-6">

        {/* Header */}
        <div className="flex flex-col items-center text-center gap-4 mb-16">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#FFBD76]">
            {t('pricing.badge')}
          </p>
          <h2
            id="comparison-heading"
            className="text-3xl md:text-[2.75rem] font-bold text-[#0A171D] tracking-[-0.02em] leading-[1.08] text-balance"
          >
            {t('comparison.heading')}
          </h2>
          <p className="max-w-xl text-base text-[#6C7377] leading-relaxed">
            {t('comparison.subheading')}
          </p>
        </div>

        {/* Table */}
        <div className="rounded-[18px] border border-[#E2E4E5] bg-white shadow-[0_4px_8px_rgba(10,23,29,0.06),0_18px_40px_rgba(10,23,29,0.12)] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[680px]">
              <caption className="sr-only">
                Feature comparison across Starter, Professional, and Enterprise plans
              </caption>

              {/* Sticky column headers */}
              <thead>
                <tr className="border-b border-[#E2E4E5]">
                  <th
                    scope="col"
                    className="py-5 px-6 text-left text-sm font-semibold text-[#6C7377] w-[40%]"
                  >
                    {t('comparison.feature')}
                  </th>
                  <th scope="col" className="py-5 px-6 text-center w-[20%]">
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-sm font-bold text-[#0A171D]">{t('pricing.starter')}</span>
                      <span className="text-xs text-[#9EA2A5] font-normal">{t('pricing.starterPrice')}</span>
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="py-5 px-6 text-center w-[20%] bg-[#003F47]"
                  >
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-sm font-bold text-[#FFFBF7]">{t('pricing.professional')}</span>
                      <span className="text-xs text-[#FFF6E9]/50 font-normal">{t('pricing.professionalPrice')}</span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded-[999px] bg-[#FFBD76] text-[#0A171D] text-[10px] font-bold uppercase tracking-[0.08em] mt-0.5">
                        {t('pricing.mostPopular')}
                      </span>
                    </div>
                  </th>
                  <th scope="col" className="py-5 px-6 text-center w-[20%]">
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-sm font-bold text-[#0A171D]">{t('pricing.enterprise')}</span>
                      <span className="text-xs text-[#9EA2A5] font-normal">{t('pricing.enterprisePrice')}</span>
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody>
                {groupsConfig.map((group) => {
                  const isOpen = expandedGroups.has(group.labelKey)
                  return (
                    <React.Fragment key={`group-${group.labelKey}`}>
                      {/* Group header row */}
                      <tr className="border-t border-[#E2E4E5]">
                        <td
                          colSpan={4}
                          className="px-6 py-0"
                        >
                          <button
                            onClick={() => toggleGroup(group.labelKey)}
                            aria-expanded={isOpen}
                            className="flex w-full items-center justify-between py-3.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#FFC992] rounded-sm"
                          >
                            <span className="text-xs font-bold uppercase tracking-[0.12em] text-[#003F47]">
                              {t(group.labelKey as any)}
                            </span>
                            <ChevronDown
                              size={16}
                              strokeWidth={2}
                              className={`text-[#9EA2A5] transition-transform duration-[220ms] ${
                                isOpen ? 'rotate-180' : 'rotate-0'
                              }`}
                              aria-hidden="true"
                            />
                          </button>
                        </td>
                      </tr>

                      {/* Feature rows */}
                      {isOpen &&
                        group.rows.map((row, i) => (
                          <tr
                            key={row.featureKey}
                            className={`border-t border-[#E2E4E5]/60 transition-colors duration-[140ms] hover:bg-[#FFF9F2] ${
                              i % 2 === 0 ? '' : 'bg-[#FAFAFA]'
                            }`}
                          >
                            {/* Feature label */}
                            <td className="py-4 px-6">
                              <div className="flex flex-col gap-0.5">
                                <span className="text-sm font-semibold text-[#16242B]">
                                  {t(row.featureKey as any)}
                                </span>
                                {row.detailKey && (
                                  <span className="text-xs text-[#9EA2A5] leading-snug">
                                    {t(row.detailKey as any)}
                                  </span>
                                )}
                              </div>
                            </td>

                            {/* Starter */}
                            <td className="py-4 px-6 text-center">
                              <Cell value={row.starter} t={t} />
                            </td>

                            {/* Professional (highlighted column) */}
                            <td className="py-4 px-6 text-center bg-[#003F47]/5 border-x border-[#003F47]/10">
                              <Cell value={row.professional} featured t={t} />
                            </td>

                            {/* Enterprise */}
                            <td className="py-4 px-6 text-center">
                              <Cell value={row.enterprise} t={t} />
                            </td>
                          </tr>
                        ))
                      }
                    </React.Fragment>
                  )
                })}
              </tbody>

              {/* CTA footer row */}
              <tfoot>
                <tr className="border-t-2 border-[#E2E4E5] bg-[#FFF9F2]">
                  <td className="py-6 px-6">
                    <span className="text-sm font-medium text-[#6C7377]">
                      {t('pricing.footer')}
                    </span>
                  </td>
                  <td className="py-6 px-6 text-center">
                    <a
                      href="#signup"
                      className="inline-flex items-center justify-center px-5 py-2.5 rounded-[999px] text-sm font-semibold bg-[#003F47] text-[#FFFBF7] hover:bg-[#00343B] transition-all duration-[220ms] shadow-[0_4px_8px_rgba(10,23,29,0.06),0_18px_40px_rgba(10,23,29,0.12)] active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC992]"
                    >
                      {t('pricing.startFreeTrial')}
                    </a>
                  </td>
                  <td className="py-6 px-6 text-center bg-[#003F47]/5 border-x border-[#003F47]/10">
                    <a
                      href="#signup"
                      className="inline-flex items-center justify-center px-5 py-2.5 rounded-[999px] text-sm font-semibold bg-[#FFBD76] text-[#0A171D] hover:bg-[#F0A24E] transition-all duration-[220ms] shadow-[0_8px_24px_rgba(255,189,118,0.35)] active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC992]"
                    >
                      {t('pricing.startFreeTrial')}
                    </a>
                  </td>
                  <td className="py-6 px-6 text-center">
                    <a
                      href="#signup"
                      className="inline-flex items-center justify-center px-5 py-2.5 rounded-[999px] text-sm font-semibold bg-[#003F47] text-[#FFFBF7] hover:bg-[#00343B] transition-all duration-[220ms] shadow-[0_4px_8px_rgba(10,23,29,0.06),0_18px_40px_rgba(10,23,29,0.12)] active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC992]"
                    >
                      {t('pricing.contactSales')}
                    </a>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </section>
  )
}

function DbCell({ items, featured }: { items: string[]; featured?: boolean }) {
  if (items.length === 0) {
    return (
      <span className="flex justify-center" aria-label="Not included">
        <span
          className={`inline-flex items-center justify-center h-6 w-6 rounded-full ${
            featured ? 'bg-[#FFBD76]/10' : 'bg-[#E4ECED]/50'
          }`}
        >
          <Minus
            size={12}
            strokeWidth={3}
            className={featured ? 'text-[#FFBD76]/40' : 'text-[#C5C8C9]'}
            aria-hidden="true"
          />
        </span>
      </span>
    )
  }

  return (
    <div className="flex flex-col items-center gap-1.5" aria-label="Included">
      {items.map((item) => (
        <div key={item} className="flex items-center gap-1.5">
          <span
            className={`inline-flex items-center justify-center h-5 w-5 shrink-0 rounded-full ${
              featured ? 'bg-[#FFBD76]/20' : 'bg-[#E4ECED]'
            }`}
          >
            <Check
              size={10}
              strokeWidth={3}
              className={featured ? 'text-[#FFBD76]' : 'text-[#003F47]'}
              aria-hidden="true"
            />
          </span>
          <span className={`text-sm font-medium ${featured ? 'text-[#FFBD76]' : 'text-[#003F47]'}`}>
            {item}
          </span>
        </div>
      ))}
    </div>
  )
}

// The admin DB stores each plan's features as free-text bullets with no
// shared feature key across plans (e.g. "Up to 10 users" vs "Unlimited
// users"), so rows are grouped by keyword category instead of exact text
// match — this keeps the grid meaningful without requiring an admin schema
// change.
const FEATURE_GROUPS: { label: string; test: RegExp }[] = [
  { label: 'Users', test: /\busers?\b/i },
  { label: 'Storage', test: /\bstorage\b|\bGB\b|\bTB\b/i },
  { label: 'Support', test: /\bsupport\b/i },
  { label: 'API Access', test: /\bapi\b/i },
  { label: 'Reports & Analytics', test: /\breports?\b|\banalytics\b|\bBI\b/i },
  { label: 'Automation', test: /\bautomation\b|\bworkflows?\b/i },
  { label: 'Security (SSO / MFA)', test: /\bsso\b|\bmfa\b|\bsaml\b/i },
  { label: 'Audit Logs', test: /\baudit\b/i },
  { label: 'Data Export', test: /\bexport\b/i },
  { label: 'Onboarding', test: /\bonboarding\b/i },
  { label: 'Branding', test: /\bbranding\b/i },
  { label: 'Custom Integrations', test: /\bintegrations?\b/i },
  { label: 'Deployment', test: /\bon-premise\b|\bon premise\b/i },
  { label: 'Modules', test: /\bmodules?\b|\bCRM\b|\bHR\b|\bpayroll\b|\bfinance\b|\binventory\b|\bwarehouse\b|\bproject management\b|\bdashboard\b/i },
]

function categorize(feature: string): string {
  const group = FEATURE_GROUPS.find((g) => g.test.test(feature))
  return group ? group.label : 'Other'
}

function DbComparisonTable() {
  const t = useT()
  const [plans, setPlans] = useState<DbPlan[] | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false
    fetch('/api/plans', { cache: 'no-store' })
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load plans')
        return res.json()
      })
      .then((data) => {
        if (!cancelled) setPlans(data.plans)
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const featuredIndex = plans
    ? plans.findIndex((p) => p.name.toLowerCase().includes('professional'))
    : -1

  const featureRows = plans
    ? Array.from(new Set(plans.flatMap((p) => p.features.map(categorize)))).sort((a, b) => {
        const order = FEATURE_GROUPS.map((g) => g.label).concat('Other')
        return order.indexOf(a) - order.indexOf(b)
      })
    : []

  return (
    <section
      id="comparison"
      className="py-24 bg-[#FFF6E9]"
      aria-labelledby="comparison-heading"
    >
      <div className="mx-auto max-w-[1200px] px-6">
        <div className="flex flex-col items-center text-center gap-4 mb-16">
          <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[#FFBD76]">
            {t('pricing.badge')}
          </p>
          <h2
            id="comparison-heading"
            className="text-3xl md:text-[2.75rem] font-bold text-[#0A171D] tracking-[-0.02em] leading-[1.08] text-balance"
          >
            {t('comparison.heading')}
          </h2>
          <p className="max-w-xl text-base text-[#6C7377] leading-relaxed">
            {t('comparison.subheading')}
          </p>
        </div>

        <div className="rounded-[18px] border border-[#E2E4E5] bg-white shadow-[0_4px_8px_rgba(10,23,29,0.06),0_18px_40px_rgba(10,23,29,0.12)] overflow-hidden">
          <div className="overflow-x-auto">
            {error && (
              <p className="p-6 text-sm text-red-600">
                Could not load plans. Please try again later.
              </p>
            )}

            {!error && !plans && (
              <p className="p-6 text-sm text-[#6C7377]">Loading plans…</p>
            )}

            {!error && plans && (
              <table className="w-full min-w-[680px]">
                <caption className="sr-only">
                  Feature comparison across subscription plans
                </caption>

                <thead>
                  <tr className="border-b border-[#E2E4E5]">
                    <th
                      scope="col"
                      className="py-5 px-6 text-left text-sm font-semibold text-[#6C7377]"
                      style={{ width: `${100 / (plans.length + 1)}%` }}
                    >
                      {t('comparison.feature')}
                    </th>
                    {plans.map((plan, i) => {
                      const featured = i === featuredIndex
                      return (
                        <th
                          key={plan.id}
                          scope="col"
                          className={`py-5 px-6 text-center ${featured ? 'bg-[#003F47]' : ''}`}
                          style={{ width: `${100 / (plans.length + 1)}%` }}
                        >
                          <div className="flex flex-col items-center gap-1">
                            <span
                              className={`text-sm font-bold ${
                                featured ? 'text-[#FFFBF7]' : 'text-[#0A171D]'
                              }`}
                            >
                              {plan.name}
                            </span>
                            <span
                              className={`text-xs font-normal ${
                                featured ? 'text-[#FFF6E9]/50' : 'text-[#9EA2A5]'
                              }`}
                            >
                              {plan.currencyCode} {plan.charge}/mo
                            </span>
                            {featured && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-[999px] bg-[#FFBD76] text-[#0A171D] text-[10px] font-bold uppercase tracking-[0.08em] mt-0.5">
                                {t('pricing.mostPopular')}
                              </span>
                            )}
                          </div>
                        </th>
                      )
                    })}
                  </tr>
                </thead>

                <tbody>
                  {featureRows.map((groupLabel, i) => (
                    <tr
                      key={groupLabel}
                      className={`border-t border-[#E2E4E5]/60 transition-colors duration-[140ms] hover:bg-[#FFF9F2] ${
                        i % 2 === 0 ? '' : 'bg-[#FAFAFA]'
                      }`}
                    >
                      <td className="py-4 px-6 align-top">
                        <span className="text-sm font-semibold text-[#16242B]">{groupLabel}</span>
                      </td>
                      {plans.map((plan, j) => {
                        const featured = j === featuredIndex
                        const items = plan.features.filter((f) => categorize(f) === groupLabel)
                        return (
                          <td
                            key={plan.id}
                            className={`py-4 px-6 text-center align-top ${
                              featured ? 'bg-[#003F47]/5 border-x border-[#003F47]/10' : ''
                            }`}
                          >
                            <DbCell items={items} featured={featured} />
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>

                <tfoot>
                  <tr className="border-t-2 border-[#E2E4E5] bg-[#FFF9F2]">
                    <td className="py-6 px-6">
                      <span className="text-sm font-medium text-[#6C7377]">{t('pricing.footer')}</span>
                    </td>
                    {plans.map((plan, i) => {
                      const featured = i === featuredIndex
                      return (
                        <td
                          key={plan.id}
                          className={`py-6 px-6 text-center ${
                            featured ? 'bg-[#003F47]/5 border-x border-[#003F47]/10' : ''
                          }`}
                        >
                          <a
                            href="#signup"
                            className={`inline-flex items-center justify-center px-5 py-2.5 rounded-[999px] text-sm font-semibold transition-all duration-[220ms] active:translate-y-px focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC992] ${
                              featured
                                ? 'bg-[#FFBD76] text-[#0A171D] hover:bg-[#F0A24E] shadow-[0_8px_24px_rgba(255,189,118,0.35)]'
                                : 'bg-[#003F47] text-[#FFFBF7] hover:bg-[#00343B] shadow-[0_4px_8px_rgba(10,23,29,0.06),0_18px_40px_rgba(10,23,29,0.12)]'
                            }`}
                          >
                            {t('pricing.startFreeTrial')}
                          </a>
                        </td>
                      )
                    })}
                  </tr>
                </tfoot>
              </table>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default function ComparisonTable() {
  return USE_HARDCODED_PLANS ? <HardcodedComparisonTable /> : <DbComparisonTable />
}
